import { useEffect, useState, useRef } from 'react'
import FallbackEditor from './FallbackEditor'

// 動態導入 Froala 相關資源
let FroalaEditorComponent: any = null
let froalaLoaded = false

interface OfficialFroalaEditorProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  placeholder?: string
  className?: string
  autoFocus?: boolean
  height?: number
  issueId?: string
  commentId?: string
}

const OfficialFroalaEditor = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder = '輸入內容...',
  className = '',
  autoFocus = false,
  height = 200,
  issueId,
  commentId
}: OfficialFroalaEditorProps) => {
  const [isReady, setIsReady] = useState(false)
  const [useFallback, setUseFallback] = useState(false)
  const editorRef = useRef<any>(null)

  useEffect(() => {
    const loadFroala = async () => {
      if (froalaLoaded && FroalaEditorComponent) {
        setIsReady(true)
        return
      }

      try {
        // 動態載入 CSS
        await import('froala-editor/css/froala_editor.pkgd.min.css')
        await import('froala-editor/css/froala_style.min.css')
        
        // 載入所有插件
        await import('froala-editor/js/plugins.pkgd.min.js')
        
        // 載入繁體中文語言包
        await import('froala-editor/js/languages/zh_tw.js')
        
        // 載入 React 組件
        const FroalaModule = await import('react-froala-wysiwyg')
        FroalaEditorComponent = FroalaModule.default
        
        froalaLoaded = true
        setIsReady(true)
      } catch (error) {
        console.error('Failed to load Froala Editor:', error)
        setUseFallback(true)
      }
    }

    loadFroala()
  }, [])

  const config = {
    key: '5OA4gC3E3F3D3C4B4E3F-11SLJCKHXOSLMc1YGSGb1ZXHSe1CgB5A4D4F4E3B2A17A19A6C4==',
    language: 'zh_tw', // 設定語言為繁體中文
    placeholderText: placeholder,
    height: height || 300,
    heightMin: 200,
    heightMax: 600,
    
    toolbarButtons: [
      ['bold', 'italic', 'underline', 'strikeThrough'],
      ['fontSize', 'textColor', 'backgroundColor'],
      ['alignLeft', 'alignCenter', 'alignRight'],
      ['formatOL', 'formatUL'],
      ['insertLink', 'insertImage', 'insertFile', 'quote'],
      ['undo', 'redo']
    ],
    
    toolbarButtonsXS: [
      ['bold', 'italic'],
      ['insertLink', 'insertImage', 'insertFile'],
      ['undo', 'redo']
    ],
    
    toolbarButtonsSM: [
      ['bold', 'italic', 'underline'],
      ['alignLeft', 'alignCenter', 'alignRight'],
      ['formatOL', 'formatUL'],
      ['insertLink', 'insertImage', 'insertFile'],
      ['undo', 'redo']
    ],
    
    // 圖片上傳配置
    imageUploadURL: '/api/upload/image',
    imageUploadParams: {
      id: 'froala_editor',
      ...(issueId && { issueId }),
      ...(commentId && { commentId })
    },
    imageUploadMethod: 'POST',
    imageMaxSize: 10 * 1024 * 1024,
    imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif', 'webp'],
    requestHeaders: {
      'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
    },
    imageEditButtons: ['imageReplace', 'imageAlign', 'imageRemove', '|', 'imageLink', 'imageDisplay', 'imageAlt', 'imageSize'],
    
    // 檔案上傳配置
    fileUploadURL: '/api/upload/file',
    fileUploadParams: {
      id: 'froala_editor',
      ...(issueId && { issueId }),
      ...(commentId && { commentId })
    },
    fileUploadMethod: 'POST',
    fileMaxSize: 50 * 1024 * 1024, // 50MB
    fileAllowedTypes: [
      // 文檔類型
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/rtf',
      'text/csv',
      // 壓縮檔案
      'application/zip',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      // 影片檔案
      'video/mp4',
      'video/avi',
      'video/quicktime',
      'video/x-ms-wmv',
      'video/x-flv',
      'video/x-matroska',
      // 音訊檔案
      'audio/mpeg',
      'audio/wav',
      'audio/ogg',
      'audio/flac'
    ],
    
    fontSize: ['12', '14', '16', '18', '20', '24'],
    charCounterCount: false,
    attribution: false,
    
    // 連結配置
    linkAlwaysBlank: true, // 所有連結都在新視窗開啟
    linkAutoPrefix: 'http://',
    linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
    linkInsertButtons: ['linkBack'],
    
    // 檔案連結配置
    fileUseSelectedText: true, // 使用選中的文字作為連結文字
    
    // 改善空白內容時的編輯體驗
    initOnClick: true,
    tabSpaces: 2,
    enter: 'P',
    pastePlain: false,
    
    events: {
      'initialized': function (this: any) {
        // Editor initialized
        editorRef.current = this
        
        // 處理現有的檔案連結，確保在新視窗開啟
        const existingFileLinks = this.el.querySelectorAll('a[href*="/uploads/files/"]')
        existingFileLinks.forEach((link: HTMLAnchorElement) => {
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener noreferrer')
        })
        
        // 如果需要自動聚焦且內容為空，確保編輯器可以被聚焦
        if (autoFocus) {
          setTimeout(() => {
            try {
              this.events.focus()
            } catch (e) {
              // 備用聚焦方法
              const editorElement = this.el
              if (editorElement) {
                editorElement.click()
              }
            }
          }, 100)
        }
      },
      
      'contentChanged': function () {
        // react-froala-wysiwyg 會自動通過 onModelChange 處理內容變更
        // 不需要手動調用 onChange
      },
      
      'image.beforeUpload': function (this: any, _images: FileList | File[] | any) {
        // 在上傳前動態設置認證 header
        const token = localStorage.getItem('auth_token')
        
        if (!token) {
          alert('請先登入後再上傳圖片')
          return false // 阻止上傳
        }
        
        // 驗證 token 格式
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          
          if (payload.exp * 1000 < Date.now()) {
            alert('登入已過期，請重新登入')
            return false
          }
        } catch (e) {
          alert('認證失效，請重新登入')
          return false
        }
        
        // 更新請求頭和參數
        this.opts.requestHeaders = {
          'Authorization': `Bearer ${token}`
        }
        
        // 更新上傳參數
        this.opts.imageUploadParams = {
          id: 'froala_editor',
          ...(issueId && { issueId }),
          ...(commentId && { commentId })
        }
        
        return true // 繼續上傳
      },
      
      'image.uploaded': function (_response: any) {
        // react-froala-wysiwyg 應該會自動觸發 onModelChange
        // 圖片上傳成功
      },
      
      'image.error': function (this: any, error: any, response: any) {
        console.error('Image upload error:', error, response)
        // 根據錯誤類型顯示相應的中文提示
        let errorMessage = '圖片上傳失敗'
        if (error && error.code) {
          switch (error.code) {
            case 1:
              errorMessage = '圖片檔案太大，請選擇較小的圖片'
              break
            case 2:
              errorMessage = '網路連線錯誤，請稍後再試'
              break
            case 3:
              errorMessage = '伺服器錯誤，請稍後再試'
              break
            case 4:
              errorMessage = '上傳被中止'
              break
            case 5:
              errorMessage = '請選擇圖片後再上傳'
              break
            case 6:
              errorMessage = '不支援的圖片格式'
              break
            case 7:
              errorMessage = '圖片上傳超時，請檢查網路連線'
              break
            default:
              errorMessage = '圖片上傳失敗，請檢查圖片格式和大小'
          }
        }
        alert(errorMessage)
      },
      
      // 檔案上傳事件
      'file.beforeUpload': function (this: any, _files: FileList | File[] | any) {
        // 在上傳前動態設置認證 header
        const token = localStorage.getItem('auth_token')
        
        if (!token) {
          alert('請先登入後再上傳檔案')
          return false // 阻止上傳
        }
        
        // 驗證 token 格式
        try {
          const payload = JSON.parse(atob(token.split('.')[1]))
          
          if (payload.exp * 1000 < Date.now()) {
            alert('登入已過期，請重新登入')
            return false
          }
        } catch (e) {
          alert('認證失效，請重新登入')
          return false
        }
        
        // 更新請求頭和參數
        this.opts.requestHeaders = {
          'Authorization': `Bearer ${token}`
        }
        
        // 更新上傳參數
        this.opts.fileUploadParams = {
          id: 'froala_editor',
          ...(issueId && { issueId }),
          ...(commentId && { commentId })
        }
        
        return true // 繼續上傳
      },
      
      'file.uploaded': function (this: any, response: any) {
        // 檔案上傳成功
        console.log('File uploaded successfully:', response)
        
        // 確保檔案連結在新視窗開啟
        setTimeout(() => {
          const fileLinks = this.el.querySelectorAll('a[href*="/uploads/files/"]')
          fileLinks.forEach((link: HTMLAnchorElement) => {
            if (!link.getAttribute('target')) {
              link.setAttribute('target', '_blank')
              link.setAttribute('rel', 'noopener noreferrer')
            }
          })
        }, 100)
      },
      
      'file.error': function (this: any, error: any, response: any) {
        console.error('File upload error:', error, response)
        // 根據錯誤類型顯示相應的中文提示
        let errorMessage = '檔案上傳失敗'
        if (error && error.code) {
          switch (error.code) {
            case 1:
              errorMessage = '檔案太大，請選擇較小的檔案'
              break
            case 2:
              errorMessage = '網路連線錯誤，請稍後再試'
              break
            case 3:
              errorMessage = '伺服器錯誤，請稍後再試'
              break
            case 4:
              errorMessage = '上傳被中止'
              break
            case 5:
              errorMessage = '請選擇檔案後再上傳'
              break
            case 6:
              errorMessage = '不支援的檔案類型'
              break
            case 7:
              errorMessage = '檔案上傳超時，請檢查網路連線'
              break
            default:
              errorMessage = '檔案上傳失敗，請檢查檔案格式和大小'
          }
        }
        alert(errorMessage)
      },
      
      'blur': function (this: any) {
        // 在 blur 時確保最新內容被傳遞
        if (this.html && this.html.get) {
          const currentContent = this.html.get()
          // 強制觸發最後一次 onChange 確保同步
          onChange(currentContent)
        }
        if (onBlur) onBlur()
      },
      
      'keydown': function (this: any, e: KeyboardEvent) {
        if (onKeyDown) {
          const reactEvent = {
            key: e.key,
            preventDefault: () => e.preventDefault(),
            stopPropagation: () => e.stopPropagation(),
            target: e.target,
            nativeEvent: e
          } as React.KeyboardEvent
          onKeyDown(reactEvent)
        }
      },
      
      'focus': function (this: any) {
        // 確保聚焦時編輯器處於可編輯狀態
        if (this.html && this.html.get() === '') {
          // 如果內容為空，設置一個空的段落標籤確保游標可見
          this.html.set('<p><br></p>')
          this.selection.setAtStart(this.el.querySelector('p'))
        }
      },
      
      'click': function (this: any) {
        // 點擊時確保編輯器獲得焦點
        this.events.focus()
      }
    }
  }

  // 如果還在載入中，顯示載入狀態
  if (!isReady && !useFallback) {
    return (
      <div className={`froala-loading ${className} border border-gray-300 rounded-md`} style={{ minHeight: height + 'px' }}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
          <div className="text-gray-500">載入編輯器中...</div>
        </div>
      </div>
    )
  }

  // 如果載入失敗，使用備用編輯器
  if (useFallback || !FroalaEditorComponent) {
    return (
      <FallbackEditor
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
        height={height}
      />
    )
  }

  // 處理點擊事件，確保編輯器能夠聚焦
  const handleWrapperClick = () => {
    if (editorRef.current) {
      try {
        // 嘗試聚焦編輯器
        editorRef.current.events.focus()
      } catch (e) {
        // 如果 focus 事件失敗，嘗試直接點擊編輯器元素
        const editorElement = editorRef.current.el
        if (editorElement) {
          const editableArea = editorElement.querySelector('.fr-element') || editorElement
          if (editableArea) {
            editableArea.focus()
          }
        }
      }
    }
  }

  return (
    <div 
      className={`official-froala-wrapper ${className}`} 
      onClick={handleWrapperClick} 
      style={{ minHeight: height + 'px' }}
    >
      <FroalaEditorComponent
        config={{
          ...config,
          events: {
            ...config.events,
            'initialized': function () {
              if (autoFocus) {
                // 如果需要自動聚焦，在初始化後聚焦編輯器
                setTimeout(() => {
                  if (editorRef.current) {
                    try {
                      editorRef.current.events.focus()
                    } catch (e) {
                      // 忽略聚焦錯誤
                    }
                  }
                }, 100)
              }
            }
          }
        }}
        model={value}
        onModelChange={(newValue: string) => {
          onChange(newValue)
        }}
        ref={editorRef}
      />
    </div>
  )
}

export default OfficialFroalaEditor
